import * as types from 'constants/ActionTypes';
import { setConfig as setApiConfig } from 'js-api-client';
import { updatePurchaseWithReserhubAccount } from './purchase';
import reserhubApi from '../services/reserhub-api';
import errorMonitoring from '../errorMonitoring';
import { updateLegacyReducersWithConnectedAccounts } from '../adapters/loyalty';
import { updateSiemprePlusData } from './siemprePlus';
import { receiveProfile } from './costapass';

/** Same key as `ReserhubAccountProvider` — cleared when unified session ends for any reason. */
const RESERHUB_LOGIN_FLAG = 'RESERHUB_USER_LOGGED_IN';

/**
 * Connected-accounts list response includes per-provider errors without failing the HTTP request.
 * @param {Object} response - API JSON body
 * @returns {boolean}
 */
function hasDotersTenantUnavailableInConnectedAccountsResponse(response) {
  return (
    Array.isArray(response?.errors) &&
    response.errors.some((e) => e?.provider === 'doters' && e?.code === 'unavailable')
  );
}

/**
 * Single-account endpoint returns HTTP 503 with JSON `{ code: "unavailable", ... }`.
 * @param {Error} error - Thrown by reserhubApi request layer
 * @returns {Promise<boolean>}
 */
async function isDotersUnavailableHttpError(error) {
  if (error?.status !== 503 || !error?.response) return false;
  try {
    const body = await error.response.json();
    return body?.code === 'unavailable';
  } catch {
    return false;
  }
}

/** Synthetic error when the connected-accounts HTTP request throws (no API body). */
const FETCH_CONNECTED_ACCOUNTS_FAILED = 'fetch_failed';

/**
 * Thunk return shape aligned with the connected-accounts API: `connectedAccounts` plus `errors`.
 * @param {Array} [connectedAccounts]
 * @param {Array} [errors]
 * @returns {{ connectedAccounts: Array, errors: Array }}
 */
function buildFetchConnectedAccountsResult(connectedAccounts = [], errors = []) {
  return {
    connectedAccounts: Array.isArray(connectedAccounts) ? connectedAccounts : [],
    errors: Array.isArray(errors) ? errors : [],
  };
}

/**
 * Show or hide the Doters-unavailable-for-tenant dialog (after forced unified logout).
 * @param {boolean} visible - Whether the dialog is visible
 * @returns {Object} The action
 */
export function setReserhubDotersUnavailableDialogVisible(visible) {
  return {
    type: types.SET_RESERHUB_DOTERS_UNAVAILABLE_DIALOG_VISIBLE,
    visible,
  };
}

/**
 * Set Reserhub user in the state
 * @param {Object} user - The user data from Reserhub
 * @returns {Object} The action
 */
export function setReserhubUser(user) {
  return {
    type: types.SET_RESERHUB_USER,
    user,
  };
}

/**
 * Clear Reserhub user from the state (logout)
 * @returns {Object} The action
 */
export function clearReserhubUser() {
  return {
    type: types.CLEAR_RESERHUB_USER,
  };
}

/**
 * Set loading state for Reserhub user data operations
 * @param {boolean} isLoading - The loading state for user data
 * @returns {Object} The action
 */
export function setReserhubUserLoading(isLoading) {
  return {
    type: types.SET_RESERHUB_USER_LOADING,
    isLoading,
  };
}

/**
 * Set Reserhub next purchase in the state
 * @param {Object} nextPurchase - The next purchase data from Reserhub
 * @returns {Object} The action
 */
export function setReserhubNextPurchase(nextPurchase) {
  return {
    type: types.SET_RESERHUB_NEXT_PURCHASE,
    nextPurchase,
  };
}

/**
 * Clear Reserhub next purchase from the state
 * @returns {Object} The action
 */
export function clearReserhubNextPurchase() {
  return {
    type: types.CLEAR_RESERHUB_NEXT_PURCHASE,
  };
}

/**
 * Set loading state for Reserhub next purchase data operations
 * @param {boolean} isLoading - The loading state for next purchase data
 * @returns {Object} The action
 */
export function setReserhubNextPurchaseLoading(isLoading) {
  return {
    type: types.SET_RESERHUB_NEXT_PURCHASE_LOADING,
    isLoading,
  };
}

/**
 * Fetch Reserhub account next purchase
 * @returns {Function} Thunk action
 */
export function fetchReserhubAccountNextPurchase() {
  return async (dispatch) => {
    dispatch(setReserhubNextPurchaseLoading(true));
    try {
      const response = await reserhubApi.getUpcomingTrips();
      const nextPurchase = response.upcomingTrips?.[0]?.purchase;
      if (nextPurchase) {
        dispatch(setReserhubNextPurchase(nextPurchase));
      }
      return nextPurchase;
    } catch (error) {
      if (error.status === 500)
        errorMonitoring.notify({
          error,
          info: {
            message: 'Error fetching Reserhub account next purchase',
          },
        });
      dispatch(clearReserhubNextPurchase());
      return null;
    } finally {
      dispatch(setReserhubNextPurchaseLoading(false));
    }
  };
}

/**
 * Set loading state for connected accounts
 * @param {boolean} isLoading - The loading state
 * @returns {Object} The action
 */
export function setReserhubConnectedAccountsLoading(isLoading) {
  return {
    type: types.SET_RESERHUB_CONNECTED_ACCOUNTS_LOADING,
    isLoading,
  };
}

/**
 * Set connected accounts in the state
 * @param {Array} connectedAccounts - The connected accounts data
 * @returns {Object} The action
 */
export function setReserhubConnectedAccounts(connectedAccounts) {
  return {
    type: types.SET_RESERHUB_CONNECTED_ACCOUNTS,
    connectedAccounts,
  };
}

/**
 * Set loading state for wallets
 * @param {boolean} isLoading - The loading state
 * @returns {Object} The action
 */
export function setReserhubWalletsLoading(isLoading) {
  return {
    type: types.SET_RESERHUB_WALLETS_LOADING,
    isLoading,
  };
}

/**
 * Set wallets in the state
 * @param {Array} wallets - The wallets data
 * @returns {Object} The action
 */
export function setReserhubWallets(wallets) {
  return {
    type: types.SET_RESERHUB_WALLETS,
    wallets,
  };
}

/**
 * Clear all Reserhub data from the state
 * @param {Object} [options]
 * @param {boolean} [options.preserveCostapassProfile] - When true, keeps CostaPass profile in state (e.g. Doters-tenant-unavailable logout).
 * @returns {Function} Thunk action
 */
export function clearAllReserhubData(options = {}) {
  const { preserveCostapassProfile = false } = options;
  return async (dispatch) => {
    localStorage.removeItem(RESERHUB_LOGIN_FLAG);
    dispatch(clearReserhubUser());
    dispatch(clearReserhubNextPurchase());
    dispatch(setReserhubConnectedAccounts([]));
    dispatch(setReserhubWallets([]));
    dispatch(updateSiemprePlusData(null, 'doters'));
    if (!preserveCostapassProfile) {
      dispatch(receiveProfile(null, 'costapass', null));
    }
    dispatch(updatePurchaseWithReserhubAccount({ accountId: null }));
    dispatch(setReserhubDotersUnavailableDialogVisible(false));
    setApiConfig({ userAuthToken: null, withCredentials: false });
  };
}

/**
 * Clear only Doters-related state and remove Doters from connected accounts (no API logout).
 * @returns {Function} Thunk action
 */
export function clearDotersFromState() {
  return (dispatch, getState) => {
    dispatch(updateSiemprePlusData(null, 'doters'));
    const connectedAccounts = getState().reserhubAccount.get('connectedAccounts') || [];
    const withoutDoters = connectedAccounts.filter((account) => account.provider !== 'doters');
    dispatch(setReserhubConnectedAccounts(withoutDoters));
  };
}

/**
 * End unified session when Doters data is not available for this tenant (503 / errors[]).
 * Shows a fixed copy alert and clears local state even if the logout request fails.
 * @returns {Function} Thunk action
 */
export function logoutReserhubDueToDotersTenantUnavailable() {
  return async (dispatch) => {
    try {
      await reserhubApi.logout();
    } catch {
      // Local state is still cleared below; API logout is best-effort.
    }
    await dispatch(clearAllReserhubData({ preserveCostapassProfile: true }));
    dispatch(setReserhubDotersUnavailableDialogVisible(true));
  };
}

/**
 * End unified session when Doters account refresh is unauthorized.
 * @returns {Function} Thunk action
 */
export function logoutReserhubDueToDotersUnauthorized() {
  return async (dispatch) => {
    try {
      await reserhubApi.logout();
    } catch {
      // Local state is still cleared below; API logout is best-effort.
    }
    await dispatch(clearAllReserhubData({ preserveCostapassProfile: true }));
  };
}

/**
 * Fetch wallets for the current user
 * @returns {Function} Thunk action
 */
export function fetchWallets() {
  return async (dispatch) => {
    dispatch(setReserhubWalletsLoading(true));
    try {
      const response = await reserhubApi.getWallets();
      const wallets = response.wallets || [];
      dispatch(setReserhubWallets(wallets));
      return wallets;
    } catch (error) {
      if (error.status === 500)
        errorMonitoring.notify({
          error,
          info: {
            message: 'Error fetching wallets',
          },
        });
      dispatch(setReserhubWallets([]));
      return null;
    } finally {
      dispatch(setReserhubWalletsLoading(false));
    }
  };
}

/**
 * Fetch a single connected account by provider and update state (e.g. to refresh Doters balance).
 * @param {string} provider - The provider to fetch (e.g. 'doters')
 * @returns {Function} Thunk action
 */
export function fetchConnectedAccountByProvider(provider) {
  return async (dispatch, getState) => {
    dispatch(setReserhubConnectedAccountsLoading(true));
    try {
      const response = await reserhubApi.getConnectedAccountByProvider({ provider });
      const account = response?.connectedAccount ?? response;
      const connectedAccounts = getState().reserhubAccount.get('connectedAccounts') || [];
      if (!account || account.provider !== provider) {
        const withoutProvider = connectedAccounts.filter((a) => a.provider !== provider);
        dispatch(setReserhubConnectedAccounts(withoutProvider));
        updateLegacyReducersWithConnectedAccounts(dispatch, withoutProvider);
        return null;
      }
      const updatedAccounts = connectedAccounts
        .filter((a) => a.provider !== provider)
        .concat(account);
      dispatch(setReserhubConnectedAccounts(updatedAccounts));
      updateLegacyReducersWithConnectedAccounts(dispatch, [account]);
      return account;
    } catch (error) {
      if (error.status === 500) {
        errorMonitoring.notify({
          error,
          info: {
            message: `Error fetching connected account for provider: ${provider}`,
          },
        });
      }
      if (provider === 'doters' && error.status === 401) {
        await dispatch(logoutReserhubDueToDotersUnauthorized());
      }
      if (provider === 'doters' && (await isDotersUnavailableHttpError(error))) {
        await dispatch(logoutReserhubDueToDotersTenantUnavailable());
      }
      return null;
    } finally {
      dispatch(setReserhubConnectedAccountsLoading(false));
    }
  };
}

/**
 * Fetch connected accounts for the current user
 * @returns {Function} Thunk resolving to `{ connectedAccounts, errors }` (same shape as the API body)
 */
export function fetchConnectedAccounts() {
  return async (dispatch) => {
    dispatch(setReserhubConnectedAccountsLoading(true));
    try {
      const response = await reserhubApi.getConnectedAccounts();
      const connectedAccounts = response.connectedAccounts || [];
      const errors = Array.isArray(response.errors) ? response.errors : [];

      if (hasDotersTenantUnavailableInConnectedAccountsResponse(response)) {
        return buildFetchConnectedAccountsResult(connectedAccounts, errors);
      }
      dispatch(setReserhubConnectedAccounts(connectedAccounts));
      return buildFetchConnectedAccountsResult(connectedAccounts, errors);
    } catch (error) {
      if (error.status === 500)
        errorMonitoring.notify({
          error,
          info: {
            message: 'Error fetching connected accounts',
          },
        });
      dispatch(setReserhubConnectedAccounts([]));
      return buildFetchConnectedAccountsResult(
        [],
        [
          {
            code: FETCH_CONNECTED_ACCOUNTS_FAILED,
            message: 'Error fetching connected accounts',
            provider: null,
            cause: error instanceof Error ? error : new Error(String(error)),
          },
        ],
      );
    } finally {
      dispatch(setReserhubConnectedAccountsLoading(false));
    }
  };
}

/**
 * Fetch Reserhub user data
 * @returns {Function} Thunk action
 */
export function fetchReserhubUser() {
  return async (dispatch) => {
    dispatch(setReserhubUserLoading(true));
    try {
      // User profile and connected accounts can be fetched in parallel.
      const [userSettled, connectedSettled] = await Promise.allSettled([
        reserhubApi.getCurrentUser(),
        dispatch(fetchConnectedAccounts()),
      ]);

      const user = userSettled.status === 'fulfilled' ? userSettled.value : null;
      const connectedFetchResult =
        connectedSettled.status === 'fulfilled' ? connectedSettled.value : null;

      if (
        connectedFetchResult &&
        hasDotersTenantUnavailableInConnectedAccountsResponse(connectedFetchResult)
      ) {
        await dispatch(logoutReserhubDueToDotersTenantUnavailable());
        return null;
      }

      const connectedAccountsFetchFailed = connectedFetchResult?.errors?.some(
        (e) => e?.code === FETCH_CONNECTED_ACCOUNTS_FAILED,
      );
      const connectedAccounts =
        connectedFetchResult && !connectedAccountsFetchFailed
          ? connectedFetchResult.connectedAccounts
          : null;

      if (user && user.id) {
        dispatch(setReserhubUser(user));
        // withCredentials config is set to false to avoid sending another session cookie to the API.
        setApiConfig({ userAuthToken: user.sessionToken, withCredentials: false });
        const connectedAccountsFetchSucceeded = Array.isArray(connectedAccounts);
        const hasDotersAccount =
          connectedAccountsFetchSucceeded && connectedAccounts.some((c) => c.provider === 'doters');

        // Only update purchase when we confirmed there is no Doters account.
        // Skipping when the fetch failed (null) avoids the same Doters conflict on the error path.
        if (connectedAccountsFetchSucceeded && !hasDotersAccount) {
          dispatch(updatePurchaseWithReserhubAccount({ accountId: user.id }));
        }

        if (connectedAccounts && connectedAccounts.length > 0) {
          updateLegacyReducersWithConnectedAccounts(dispatch, connectedAccounts);
        }
      } else {
        setApiConfig({ userAuthToken: null, withCredentials: false });
      }

      if (connectedSettled.status === 'rejected') {
        errorMonitoring.notify({
          error: connectedSettled.reason,
          info: {
            message: 'Error fetching unified account data during user fetch',
          },
        });
      } else if (connectedAccountsFetchFailed) {
        const fetchFailedEntry = connectedFetchResult?.errors?.find(
          (e) => e?.code === FETCH_CONNECTED_ACCOUNTS_FAILED,
        );
        const { cause } = fetchFailedEntry || {};
        const alreadyNotifiedFor500 = cause instanceof Error && cause.status === 500;
        if (!alreadyNotifiedFor500) {
          const errorForMonitoring =
            cause instanceof Error
              ? cause
              : new Error(fetchFailedEntry?.message || 'Error fetching connected accounts');
          errorMonitoring.notify({
            error: errorForMonitoring,
            info: {
              message: 'Error fetching unified account data during user fetch',
            },
          });
        }
      }

      return user;
    } catch (error) {
      if (error.status === 500)
        errorMonitoring.notify({
          error,
          info: {
            message: 'Error fetching Reserhub user',
          },
        });

      setApiConfig({ userAuthToken: null, withCredentials: false });
      dispatch(updatePurchaseWithReserhubAccount({ accountId: null }));
    } finally {
      dispatch(setReserhubUserLoading(false));
    }
  };
}

/**
 * Logout the Reserhub user
 * @returns {Function} Thunk action
 */
export function logoutReserhubUser() {
  return async (dispatch) => {
    dispatch(setReserhubUserLoading(true));
    try {
      const success = await reserhubApi.logout();

      if (success) {
        setApiConfig({ userAuthToken: null, withCredentials: false });
        dispatch(clearAllReserhubData());
      }

      return success;
    } catch (error) {
      if (error.status === 500)
        errorMonitoring.notify({
          error,
          info: {
            message: 'Error logging out Reserhub user',
          },
        });

      // Clear next purchase data even if logout fails
      dispatch(clearReserhubNextPurchase());
      return false;
    } finally {
      dispatch(setReserhubUserLoading(false));
    }
  };
}
